import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { getApiUrl } from '../config/api';
import { checkPermission } from '../utils/permissions';
import { InventorySearchService, InventoryItem } from '../services/inventorySearchService';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import AccessDenied from './AccessDenied';
import LoadingSpinner from './LoadingSpinner';
import Toast from './Toast';
import { BarcodeScanner } from './BarcodeScanner';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Dashboard: React.FC = () => {
  const { user, token } = useAuth();
  const { selectedBranchId, selectedBranchName, isAllBranches } = useBranch();
  const location = useLocation();
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('This Month');
  const [isTimeFilterOpen, setIsTimeFilterOpen] = useState(false);
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const [showBalances, setShowBalances] = useState(true);
  const [isLoadingBalancesSetting, setIsLoadingBalancesSetting] = useState(false);
  const [companyData, setCompanyData] = useState<any>(null);
  
  // Refs for dropdown outside click handling
  const timeFilterRef = useRef<HTMLDivElement>(null);
  
  // Dashboard data state
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  // const [dashboardError, setDashboardError] = useState<string | null>(null);
  
  // Day activity tables state (independent of main date filter)
  const [inventoryDayFilter, setInventoryDayFilter] = useState<'today' | 'yesterday'>('today');
  const [salesDayFilter, setSalesDayFilter] = useState<'today' | 'yesterday'>('today');
  const [isInventoryDayFilterOpen, setIsInventoryDayFilterOpen] = useState(false);
  const [isSalesDayFilterOpen, setIsSalesDayFilterOpen] = useState(false);
  const [dayInventoryData, setDayInventoryData] = useState<any[]>([]);
  const [daySalesData, setDaySalesData] = useState<any[]>([]);
  const [isLoadingInventoryActivity, setIsLoadingInventoryActivity] = useState(false);
  const [isLoadingSalesActivity, setIsLoadingSalesActivity] = useState(false);
  

  // Load show_balances setting from database
  const loadShowBalancesSetting = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(getApiUrl("companies"), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.company) {
          setCompanyData(data.company);
          setShowBalances(data.company.show_balances ?? true);
        }
      }
    } catch (error) {
      console.error('Error loading show balances setting:', error);
    }
  };

  // Save show_balances setting to database
  const saveShowBalancesSetting = async (value: boolean) => {
    if (!token || isLoadingBalancesSetting || !companyData?.id) return;
    
    setIsLoadingBalancesSetting(true);
    try {
      const response = await fetch(`${getApiUrl("companies/show-balances")}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ companyId: companyData.id, showBalances: value })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setShowBalances(value);
          console.log('Show balances setting saved:', value);
        }
      } else {
        console.error('Failed to save show balances setting');
        // Revert the UI state if save failed
        setShowBalances(!value);
      }
    } catch (error) {
      console.error('Error saving show balances setting:', error);
      // Revert the UI state if save failed
      setShowBalances(!value);
    } finally {
      setIsLoadingBalancesSetting(false);
    }
  };

  // Handle toggle change
  const handleShowBalancesToggle = () => {
    const newValue = !showBalances;
    saveShowBalancesSetting(newValue);
  };

  // Fetch dashboard data from API
  const fetchDashboardData = useCallback(async () => {
    if (!token) {
      return;
    }
    
    setIsLoadingDashboard(true);
    // setDashboardError(null);
    
    try {
      const params = new URLSearchParams({
        date_range: selectedTimeFilter,
        ...(selectedBranchId && !isAllBranches ? { branch_id: selectedBranchId.toString() } : {}),
        ...(isCustomDateRange && customStartDate && customEndDate ? {
          start_date: customStartDate,
          end_date: customEndDate
        } : {})
      });
      
      console.log('🔍 [DASHBOARD] Branch selection debug:', {
        selectedBranchId,
        selectedBranchName,
        isAllBranches,
        userRole: user?.role,
        willSendBranchId: selectedBranchId && !isAllBranches
      });
      
      const apiUrl = getApiUrl('dashboard') + '?' + params.toString();
      console.log('🔍 [DASHBOARD] API URL:', apiUrl);
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to fetch dashboard data`);
      }

      const result = await response.json();
      
      console.log('🔍 [DASHBOARD] API Response:', result);
      
      // Use API data directly if available, otherwise use minimal defaults
      const apiData = result.data || {};
      
      // Only use defaults for missing fields, not to override API data
      const mergedData = {
        balances: {
          openingBalance: 0,
          balanceDate: new Date().toISOString().split('T')[0],
          isToday: true,
          ...apiData.balances
        },
        customers: {
          totalRevenue: 0,
          totalProfit: 0,
          totalSales: 0,
          revenueChange: 0,
          profitChange: 0,
          salesChange: 0,
          ...apiData.customers
        },
        dealers: {
          totalRevenue: 0,
          totalProfit: 0,
          totalSales: 0,
          totalOutstanding: 0,
          revenueChange: 0,
          profitChange: 0,
          salesChange: 0,
          ...apiData.dealers
        },
        inventory: {
          readyToSell: 0,
          totalStock: 0,
          inService: 0,
          totalSold: 0,
          ...apiData.inventory
        },
        chartData: {
          customerSales: 0,
          dealerSales: 0,
          current: { income: 0, sales: 0, inventory: 0 },
          comparison: { income: 0, sales: 0, inventory: 0 },
          ...apiData.chartData
        }
      };
      
      console.log('🔍 [DASHBOARD] Merged Data:', mergedData);
      setDashboardData(mergedData);

    } catch (err) {
      console.error('Dashboard API error:', err);
      
      // Provide minimal fallback data when API fails
      const fallbackData = {
        balances: {
          openingBalance: 0,
          balanceDate: new Date().toISOString().split('T')[0],
          isToday: true
        },
        customers: {
          totalRevenue: 0,
          totalProfit: 0,
          totalSales: 0,
          revenueChange: 0,
          profitChange: 0,
          salesChange: 0
        },
        dealers: {
          totalRevenue: 0,
          totalProfit: 0,
          totalSales: 0,
          totalOutstanding: 0,
          revenueChange: 0,
          profitChange: 0,
          salesChange: 0
        },
        inventory: {
          readyToSell: 0,
          totalStock: 0,
          inService: 0,
          totalSold: 0
        },
        chartData: {
          customerSales: 0,
          dealerSales: 0,
          current: { income: 0, sales: 0, inventory: 0 },
          comparison: { income: 0, sales: 0, inventory: 0 }
        },
      };
      
      console.log('🔍 [DASHBOARD] Using fallback data due to API error');
      setDashboardData(fallbackData);
      
      if (err instanceof TypeError && err.message.includes('fetch')) {
        // setDashboardError('Unable to connect to server. Please check if the backend service is running.');
      } else {
        // setDashboardError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      }
    } finally {
      setIsLoadingDashboard(false);
    }
  }, [token, selectedTimeFilter, selectedBranchId, isAllBranches, isCustomDateRange, customStartDate, customEndDate]);

  // Fetch inventory activity data (independent of main date filter)
  const fetchInventoryActivity = useCallback(async () => {
    if (!token) return;
    
    setIsLoadingInventoryActivity(true);
    console.log('🔍 [INVENTORY ACTIVITY] Fetching data for:', inventoryDayFilter);
    
    try {
      const targetDate = inventoryDayFilter === 'today' 
        ? new Date().toISOString().split('T')[0]
        : new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      console.log('🔍 [INVENTORY ACTIVITY] Target date:', targetDate);
      
      // Fetch inventory data using existing inventory endpoint
      const inventoryParams = new URLSearchParams({
        limit: '50',
        page: '1'
      });
      
      const inventoryUrl = `${getApiUrl('inventory')}?${inventoryParams}`;
      console.log('🔍 [INVENTORY ACTIVITY] URL:', inventoryUrl);
      
      const inventoryResponse = await fetch(inventoryUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (inventoryResponse.ok) {
        const inventoryResult = await inventoryResponse.json();
        console.log('🔍 [INVENTORY ACTIVITY] Response:', inventoryResult);
        
        // Filter inventory for the selected day
        const inventory = inventoryResult.inventory || [];
        const filteredInventory = inventory.filter((item: any) => {
          const itemDate = new Date(item.created_at).toISOString().split('T')[0];
          return itemDate === targetDate;
        });
        
        setDayInventoryData(filteredInventory);
        console.log('🔍 [INVENTORY ACTIVITY] Filtered:', filteredInventory.length, 'items');
      } else {
        console.error('🔍 [INVENTORY ACTIVITY] Response not ok:', inventoryResponse.status);
        setDayInventoryData([]);
      }
      
    } catch (error) {
      console.error('🔍 [INVENTORY ACTIVITY] Error:', error);
      setDayInventoryData([]);
    } finally {
      setIsLoadingInventoryActivity(false);
    }
  }, [token, inventoryDayFilter]);

  // Fetch sales activity data (independent of main date filter)
  const fetchSalesActivity = useCallback(async () => {
    if (!token) return;
    
    setIsLoadingSalesActivity(true);
    console.log('🔍 [SALES ACTIVITY] Fetching data for:', salesDayFilter);
    
    try {
      const targetDate = salesDayFilter === 'today' 
        ? new Date().toISOString().split('T')[0]
        : new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      console.log('🔍 [SALES ACTIVITY] Target date:', targetDate);
      
      // Fetch sales data using existing sales endpoint
      const salesUrl = `${getApiUrl('sales')}`;
      console.log('🔍 [SALES ACTIVITY] URL:', salesUrl);
      
      const salesResponse = await fetch(salesUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (salesResponse.ok) {
        const salesResult = await salesResponse.json();
        console.log('🔍 [SALES ACTIVITY] Response:', salesResult);
        
        // Filter sales for the selected day
        const sales = salesResult.sales || [];
        const filteredSales = sales.filter((sale: any) => {
          const saleDate = new Date(sale.created_at).toISOString().split('T')[0];
          return saleDate === targetDate;
        });
        
        setDaySalesData(filteredSales);
        console.log('🔍 [SALES ACTIVITY] Filtered:', filteredSales.length, 'items');
        console.log('🔍 [SALES ACTIVITY] Sample sale data:', filteredSales[0]);
      } else {
        console.error('🔍 [SALES ACTIVITY] Response not ok:', salesResponse.status);
        setDaySalesData([]);
      }
      
    } catch (error) {
      console.error('🔍 [SALES ACTIVITY] Error:', error);
      setDaySalesData([]);
    } finally {
      setIsLoadingSalesActivity(false);
    }
  }, [token, salesDayFilter]);

  // Watch dashboardData changes
  useEffect(() => {
    // Dashboard data updated
  }, [dashboardData]);

  // Debug branch selection changes
  useEffect(() => {
    console.log('🔍 [DASHBOARD] Branch selection changed:', {
      selectedBranchId,
      selectedBranchName,
      isAllBranches,
      userRole: user?.role
    });
  }, [selectedBranchId, selectedBranchName, isAllBranches, user?.role]);

  // Load initial settings on component mount
  useEffect(() => {
    if (token) {
      loadShowBalancesSetting();
      fetchDashboardData();
    }
  }, [token, fetchDashboardData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch dashboard data when filters change
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData, selectedTimeFilter, selectedBranchId, isAllBranches]);

  // Fetch inventory activity data when inventory day filter changes
  useEffect(() => {
    fetchInventoryActivity();
  }, [fetchInventoryActivity]);

  // Fetch sales activity data when sales day filter changes
  useEffect(() => {
    fetchSalesActivity();
  }, [fetchSalesActivity]);

  // Handle outside clicks for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Close time filter dropdown if clicking outside
      if (timeFilterRef.current && !timeFilterRef.current.contains(target)) {
        setIsTimeFilterOpen(false);
      }
      
    };

    // Add event listener when dropdowns are open
    if (isTimeFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup function
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTimeFilterOpen]);

  // Fetch branches for new sale modal
  useEffect(() => {
    const fetchBranches = async () => {
      if (!token) return;
      
      try {
        const response = await fetch(`${getApiUrl("branches")}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setBranches(data.branches.map((branch: any) => ({
              id: branch.id.toString(),
              name: branch.branch_name
            })));
          }
        }
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      }
    };

    fetchBranches();
  }, [token]);

  // Initialize branch for branch managers
  useEffect(() => {
    if (user?.role === 'branch_manager' && selectedBranchId) {
      setNewSaleForm(prev => ({
        ...prev,
        branch_id: selectedBranchId
      }));
    }
  }, [user, selectedBranchId]);
  
  // Format numbers based on showBalances setting
  const formatBalance = (amount: string | number): string => {
    if (showBalances) {
      const num = typeof amount === 'string' ? parseFloat(amount) : amount;
      return isNaN(num) ? String(amount) : formatCurrency(num);
    }

    // Convert to string if it's a number and remove currency symbol
    const symbol = getCurrencySymbol();
    const amountStr = typeof amount === 'string'
      ? amount.replace(symbol, '')
      : amount.toString();

    // Replace all digits with X
    const hiddenAmount = amountStr.replace(/\d/g, 'X');

    return `${symbol}${hiddenAmount}`;
  };
  

  // Quick action modal states
  const [isAddSaleModalOpen, setIsAddSaleModalOpen] = useState(false);
  const [isAddInventoryModalOpen, setIsAddInventoryModalOpen] = useState(false);
  
  // Modal dropdown states for Add Inventory
  const [isModalCategoryDropdownOpen, setIsModalCategoryDropdownOpen] = useState(false);
  const [isModalBranchDropdownOpen, setIsModalBranchDropdownOpen] = useState(false);
  const [isModalStatusDropdownOpen, setIsModalStatusDropdownOpen] = useState(false);
  const [isModalPaymentMethodDropdownOpen, setIsModalPaymentMethodDropdownOpen] = useState(false);
  
  // Modal form states for Add Inventory
  const [selectedModalCategory, setSelectedModalCategory] = useState('mobile_phone');
  const [selectedModalBranch, setSelectedModalBranch] = useState('');
  const [selectedModalStatus, setSelectedModalStatus] = useState('ready');
  const [selectedModalPaymentMethod, setSelectedModalPaymentMethod] = useState('cash');
  const [selectedSupplier, setSelectedSupplier] = useState<number | null>(null);
  const [isNewSupplier, setIsNewSupplier] = useState(false);
  const [isSupplierDropdownOpen, setIsSupplierDropdownOpen] = useState(false);
  
  // Inventory form data
  const [inventoryFormData, setInventoryFormData] = useState({
    imei_mac_address: '',
    phone_model: '',
    buying_price: '',
    selling_price: '',
    acquisition_notes: '',
    supplier_name: '',
    supplier_phone: '',
    supplier_aadhar: '',
    supplier_address: ''
  });
  
  // Suppliers state
  const [suppliers, setSuppliers] = useState<Array<{id: number, name: string, phone_number: string, aadhar?: string, address?: string}>>([]);
  const [isLoadingInventorySubmit, setIsLoadingInventorySubmit] = useState(false);

  // IMEI validation state
  const [imeiError, setImeiError] = useState('');

  const validateImeiOnBlur = () => {
    const requiresValidation = selectedModalCategory === 'mobile_phone' || selectedModalCategory === 'tablet';
    if (requiresValidation && inventoryFormData.imei_mac_address) {
      const digits = inventoryFormData.imei_mac_address.replace(/\D/g, '').length;
      if (digits !== 15) {
        setImeiError(`IMEI must be 15 digits (currently ${digits})`);
      } else {
        setImeiError('');
      }
    } else {
      setImeiError('');
    }
  };

  // Supplier search states
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [supplierSearchResults, setSupplierSearchResults] = useState<Array<{id: number, name: string, phone_number: string, aadhar?: string, address?: string}>>([]);
  const [hasSearchedSupplier, setHasSearchedSupplier] = useState(false);
  
  // Modal dropdown states for Add Sale
  const [isModalSalePaymentMethodDropdownOpen, setIsModalSalePaymentMethodDropdownOpen] = useState(false);
  const [isModalSaleBranchDropdownOpen, setIsModalSaleBranchDropdownOpen] = useState(false);
  
  // Types for new sale functionality
  // InventoryItem is now imported from inventorySearchService

  interface CartItem {
    inventory_item: InventoryItem;
    selling_price: number;
    profit: number;
  }

  interface NewSaleForm {
    customer: {
      name: string;
      phone: string;
      aadhar: string;
      address: string;
    };
    cart_items: CartItem[];
    total_amount: number;
    final_amount: number;
    total_profit: number;
    payment_method: string;
    branch_id: string;
    sale_date: string;
    notes: string;
  }

  // Modal form states for Add Sale
  const [inventorySearchTerm, setInventorySearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<InventoryItem[]>([]);
  const [availableInventory, setAvailableInventory] = useState<InventoryItem[]>([]);
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
  
  // Barcode scanner states - automatically active when modal is open
  const [lastScannedValue, setLastScannedValue] = useState<string>('');
  const [branches, setBranches] = useState<Array<{id: string, name: string}>>([]);
  
  // Toast states
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  const [newSaleForm, setNewSaleForm] = useState<NewSaleForm>({
    customer: {
      name: '',
      phone: '',
      aadhar: '',
      address: ''
    },
    cart_items: [],
    total_amount: 0,
    final_amount: 0,
    total_profit: 0,
    payment_method: 'cash',
    branch_id: '',
    sale_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const timeFilterOptions = [
    'This Month',
    'Last Month',
    'This Year',
    'Last Year',
    'Custom'
  ];
  
  // Modal dropdown options for Add Inventory
  // Modal dropdown options for Add Inventory
  const modalCategoryOptions = [
    { value: 'mobile_phone', label: 'Mobile Phone' },
    { value: 'laptop', label: 'Laptop' },
    { value: 'tablet', label: 'Tablet' },
    { value: 'accessories', label: 'Accessories' }
  ];
  
  const modalStatusOptions = [
    { value: 'ready', label: 'Ready' },
    { value: 'service', label: 'Service' },
    { value: 'reserved', label: 'Reserved' }
  ];
  
  const modalPaymentMethodOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'upi', label: 'UPI' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'card', label: 'Card' },
    { value: 'cheque', label: 'Cheque' }
  ];
  
  // Modal dropdown options for Add Sale  
  const modalSalePaymentMethodOptions = ['Cash', 'UPI', 'Bank Transfer', 'Credit Card', 'Debit Card'];
  const modalSaleBranchOptions = ['Main Branch', 'Branch 2', 'Branch 3', 'Branch 4', 'Branch 5'];

  // Function to get chart data based on selected time filter
  const getChartData = (timeFilter: string) => {
    // Use dynamic data if API call was successful (even if values are 0), otherwise fallback to static data
    if (dashboardData && !isLoadingDashboard) {
      // const chartData = dashboardData.chartData;
      
      const getComparisonPeriodLabel = (filter: string): string => {
        const labelMap: { [key: string]: string } = {
          'This Month': 'Last Month',
          'Last Month': 'Previous Month',
          'This Year': 'Last Year',
          'Last Year': 'Previous Year',
          'Custom': 'Previous Period'
        };
        return labelMap[filter] || 'Previous Period';
      };

      return {
        labels: ['Income', 'Sales', 'Inventory'],
        datasets: [
          {
            label: timeFilter,
            data: [
              (dashboardData?.chartData?.current?.income || 0) / 1000, 
              (dashboardData?.chartData?.current?.sales || 0) / 1000, 
              (dashboardData?.chartData?.current?.inventory || 0) / 1000
            ],
            backgroundColor: '#334155',
            borderRadius: 4,
          },
          {
            label: getComparisonPeriodLabel(timeFilter),
            data: [
              (dashboardData?.chartData?.comparison?.income || 0) / 1000, 
              (dashboardData?.chartData?.comparison?.sales || 0) / 1000, 
              (dashboardData?.chartData?.comparison?.inventory || 0) / 1000
            ],
            backgroundColor: '#94a3b8',
            borderRadius: 4,
          }
        ],
      };
    }

    // Fallback static data when API data is not available
    const currentPeriodData = {
      'This Month': { income: 520000, sales: 410000, inventory: 280000 },
      'Last Month': { income: 680000, sales: 530000, inventory: 350000 },
      'This Year': { income: 4200000, sales: 3300000, inventory: 2100000 },
      'Last Year': { income: 5800000, sales: 4500000, inventory: 2900000 }
    };

    const comparisonPeriodData = {
      'This Month': { income: 590000, sales: 465000, inventory: 310000 },
      'Last Month': { income: 620000, sales: 485000, inventory: 325000 },
      'This Year': { income: 5200000, sales: 4100000, inventory: 2650000 },
      'Last Year': { income: 4900000, sales: 3800000, inventory: 2400000 }
    };

    const current = currentPeriodData[timeFilter as keyof typeof currentPeriodData] || currentPeriodData['This Month'];
    const comparison = comparisonPeriodData[timeFilter as keyof typeof comparisonPeriodData] || comparisonPeriodData['This Month'];

    const getComparisonPeriodLabel = (filter: string): string => {
      const labelMap: { [key: string]: string } = {
        'This Month': 'Last Month',
        'Last Month': 'Previous Month',
        'This Year': 'Last Year',
        'Last Year': 'Previous Year',
        'Custom': 'Previous Period'
      };
      return labelMap[filter] || 'Previous Period';
    };

    return {
      labels: ['Income', 'Sales', 'Inventory'],
      datasets: [
        {
          label: timeFilter,
          data: [current.income / 1000, current.sales / 1000, current.inventory / 1000],
          backgroundColor: '#334155',
          borderRadius: 4,
        },
        {
          label: getComparisonPeriodLabel(timeFilter),
          data: [comparison.income / 1000, comparison.sales / 1000, comparison.inventory / 1000],
          backgroundColor: '#94a3b8',
          borderRadius: 4,
        }
      ],
    };
  };

  const monthlyData = getChartData(selectedTimeFilter);

  // Generate customer vs dealer data with conditional formatting
  const customerVsDealerData = React.useMemo(() => {
    // Use dynamic data if API call was successful (even if values are 0), otherwise fallback to static data
    const customerSalesValue = (dashboardData && !isLoadingDashboard) 
      ? (dashboardData?.chartData?.customerSales || 0)
      : 385000;
    const dealerSalesValue = (dashboardData && !isLoadingDashboard) 
      ? (dashboardData?.chartData?.dealerSales || 0)
      : 167000;
    
    return {
      labels: [
        `Customer Sales ${showBalances ? `(${formatCurrency(customerSalesValue)})` : `(${getCurrencySymbol()}X,XX,XXX)`}`,
        `Dealer Sales ${showBalances ? `(${formatCurrency(dealerSalesValue)})` : `(${getCurrencySymbol()}X,XX,XXX)`}`
      ],
      datasets: [
        {
          data: showBalances ? [customerSalesValue, dealerSalesValue] : [1, 1], // Show equal segments when hidden
          backgroundColor: ['#334155', '#94a3b8'],
          borderWidth: 0,
        },
      ],
    };
  }, [showBalances, dashboardData, isLoadingDashboard]); // Regenerate when showBalances, dashboardData, or loading state changes

  const InfoIcon: React.FC<{ helpText?: string }> = ({ helpText }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    
    if (!helpText) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    
    return (
      <div className="relative">
        <svg 
          className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        
        {showTooltip && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg z-[9999]">
            <div className="whitespace-normal leading-relaxed">{helpText}</div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
          </div>
        )}
      </div>
    );
  };

  const TrendingUpIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );

  const TrendingDownIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
    </svg>
  );



  const MoreHorizontalIcon = () => (
    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01" />
    </svg>
  );

  // Function to get comparison label based on selected time filter
  const getComparisonLabel = (timeFilter: string): string => {
    const comparisonMap: { [key: string]: string } = {
      'This Month': 'Compared to last month',
      'Last Month': 'Compared to previous month',
      'This Year': 'Compared to last year',
      'Last Year': 'Compared to previous year'
    };
    return comparisonMap[timeFilter] || 'Compared to previous period';
  };

  // Toast functionality
  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    setToast({ message, type, isVisible: true });
    setTimeout(() => {
      setToast(prev => ({ ...prev, isVisible: false }));
    }, 5000);
  };

  // Close all modal dropdowns
  const closeAllModalDropdowns = () => {
    setIsModalCategoryDropdownOpen(false);
    setIsModalBranchDropdownOpen(false);
    setIsModalStatusDropdownOpen(false);
    setIsModalPaymentMethodDropdownOpen(false);
    setIsModalSalePaymentMethodDropdownOpen(false);
    setIsModalSaleBranchDropdownOpen(false);
    setIsSupplierDropdownOpen(false);
  };
  
  // Helper functions for inventory modal
  const getSelectedCategoryLabel = () => {
    const option = modalCategoryOptions.find(opt => opt.value === selectedModalCategory);
    return option ? option.label : 'Select Category';
  };
  
  const getSelectedStatusLabel = () => {
    const option = modalStatusOptions.find(opt => opt.value === selectedModalStatus);
    return option ? option.label : 'Select Status';
  };
  
  const getSelectedPaymentLabel = () => {
    const option = modalPaymentMethodOptions.find(opt => opt.value === selectedModalPaymentMethod);
    return option ? option.label : 'Select Payment Method';
  };
  
  const getBranchNameById = (branchId: string) => {
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : 'Select Branch';
  };
  
  // Reset inventory modal form
  const resetInventoryForm = () => {
    setInventoryFormData({
      imei_mac_address: '',
      phone_model: '',
      buying_price: '',
      selling_price: '',
      acquisition_notes: '',
      supplier_name: '',
      supplier_phone: '',
      supplier_aadhar: '',
      supplier_address: ''
    });
    setSelectedModalCategory('mobile_phone');
    setSelectedModalBranch('');
    setSelectedModalStatus('ready');
    setSelectedModalPaymentMethod('cash');
    setSelectedSupplier(null);
    setIsNewSupplier(false);
    // Reset supplier search state
    setSupplierSearchTerm('');
    setSupplierSearchResults([]);
    setHasSearchedSupplier(false);
    setImeiError('');
    closeAllModalDropdowns();
  };

  // Handle inventory modal close
  const handleInventoryModalClose = () => {
    setIsAddInventoryModalOpen(false);
    resetInventoryForm();
  };

  // Supplier search function - searches by name, phone, or aadhar
  const handleSupplierSearch = () => {
    const searchTerm = supplierSearchTerm.trim().toLowerCase();

    if (!searchTerm) {
      setSupplierSearchResults([]);
      setHasSearchedSupplier(false);
      return;
    }

    setHasSearchedSupplier(true);

    // Client-side search through suppliers
    const results = suppliers.filter(supplier => {
      const nameMatch = supplier.name?.toLowerCase().includes(searchTerm);
      const phoneMatch = supplier.phone_number?.toLowerCase().includes(searchTerm);
      const aadharMatch = supplier.aadhar?.toLowerCase().includes(searchTerm);
      return nameMatch || phoneMatch || aadharMatch;
    });

    setSupplierSearchResults(results);
  };

  // Clear selected supplier
  const handleClearSupplier = () => {
    setSelectedSupplier(null);
    setSupplierSearchTerm('');
    setSupplierSearchResults([]);
    setHasSearchedSupplier(false);
  };

  // Auto-fill supplier details when phone number is 10 digits
  const handleSupplierPhoneChange = (phone: string) => {
    setInventoryFormData(prev => ({...prev, supplier_phone: phone}));

    // Extract only digits
    const digitsOnly = phone.replace(/\D/g, '');

    // When phone reaches 10 digits, search for existing supplier
    if (digitsOnly.length === 10) {
      const existingSupplier = suppliers.find(s => {
        const supplierDigits = s.phone_number?.replace(/\D/g, '') || '';
        return supplierDigits === digitsOnly || supplierDigits.endsWith(digitsOnly);
      });

      if (existingSupplier) {
        setInventoryFormData(prev => ({
          ...prev,
          supplier_phone: phone,
          supplier_name: existingSupplier.name || '',
          supplier_aadhar: existingSupplier.aadhar || '',
          supplier_address: existingSupplier.address || ''
        }));
        showToast(`Found existing supplier: ${existingSupplier.name}`, 'info');
      }
    }
  };
  
  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${getApiUrl("suppliers")}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch suppliers');
      }
      
      const data = await response.json();
      setSuppliers(data.suppliers || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      showToast('Failed to load suppliers', 'error');
    }
  };
  
  // Handle inventory form submission
  const handleSubmitInventory = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate IMEI for mobile phones and tablets (must be exactly 15 digits)
    if ((selectedModalCategory === 'mobile_phone' || selectedModalCategory === 'tablet')) {
      const imeiDigits = inventoryFormData.imei_mac_address.replace(/\D/g, '');
      if (imeiDigits.length !== 15) {
        setImeiError(`IMEI must be 15 digits (currently ${imeiDigits.length})`);
        return;
      }
    }

    try {
      setIsLoadingInventorySubmit(true);

      const payload: any = {
        date_added: new Date().toISOString().split('T')[0],
        category: selectedModalCategory,
        phone_model: inventoryFormData.phone_model,
        imei_mac_address: inventoryFormData.imei_mac_address,
        buying_price: parseFloat(inventoryFormData.buying_price),
        service_expense: 0,
        package_expense: 0,
        selling_price: parseFloat(inventoryFormData.selling_price) || 0,
        branch_id: parseInt(selectedModalBranch),
        status: selectedModalStatus,
        payment_method: selectedModalPaymentMethod,
        acquisition_notes: inventoryFormData.acquisition_notes || null
      };
      
      // Handle supplier
      if (selectedSupplier) {
        payload.supplier_id = selectedSupplier;
      } else if (isNewSupplier && inventoryFormData.supplier_name && inventoryFormData.supplier_phone) {
        payload.supplier_info = {
          name: inventoryFormData.supplier_name,
          phone_number: inventoryFormData.supplier_phone,
          aadhar: inventoryFormData.supplier_aadhar || null,
          address: inventoryFormData.supplier_address || null
        };
      }
      
      const response = await fetch(`${getApiUrl("inventory")}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add inventory item');
      }
      
      const result = await response.json();
      showToast('Inventory item added successfully!', 'success');
      handleInventoryModalClose();
      
      // Refresh dashboard data
      await fetchDashboardData();
      
    } catch (error: any) {
      console.error('Error adding inventory:', error);
      showToast(error.message || 'Failed to add inventory item', 'error');
    } finally {
      setIsLoadingInventorySubmit(false);
    }
  };

  // Helper function for inventory search that can be used by both manual and barcode
  const performInventorySearch = async (searchTerm: string) => {
    const trimmedSearchTerm = searchTerm.trim();
    
    if (!token || !trimmedSearchTerm) {
      showToast('Please enter a search term', 'warning');
      return;
    }

    try {
      setIsLoadingInventory(true);
      setHasSearched(true);
      
      const result = await InventorySearchService.getAvailableInventory(token, trimmedSearchTerm, {
        branchId: selectedBranchId || undefined,
        excludeCartItems: cartItems,
        userRole: user?.role,
        isAllBranches
      });

      if (result.success) {
        setSearchResults(result.items);
      } else {
        showToast(result.error || 'Search failed', 'error');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Inventory search error:', error);
      showToast('An error occurred while searching', 'error');
      setSearchResults([]);
    } finally {
      setIsLoadingInventory(false);
    }
  };

  // Manual search function
  const handleInventorySearch = async () => {
    await performInventorySearch(inventorySearchTerm);
  };

  // Barcode scan handler - just sets the search term and triggers the same search logic
  const handleBarcodeScan = async (scannedValue: string) => {
    console.log('🔍 Barcode scanned in Dashboard:', scannedValue);
    
    // Prevent duplicate scans
    if (scannedValue === lastScannedValue) {
      return;
    }
    
    // Set the scanned value and track it
    setInventorySearchTerm(scannedValue);
    setLastScannedValue(scannedValue);
    
    // Use the same search logic as manual search
    await performInventorySearch(scannedValue);
  };

  const addToCart = (inventoryItem: InventoryItem, sellingPrice: number) => {
    const totalCost = (typeof inventoryItem.buying_price === 'string' ? parseFloat(inventoryItem.buying_price) : inventoryItem.buying_price) + (typeof inventoryItem.service_expense === 'string' ? parseFloat(inventoryItem.service_expense) : inventoryItem.service_expense) + (typeof inventoryItem.package_expense === 'string' ? parseFloat(inventoryItem.package_expense) : inventoryItem.package_expense);
    const profit = sellingPrice - totalCost;
    
    const cartItem: CartItem = {
      inventory_item: inventoryItem,
      selling_price: sellingPrice,
      profit: profit
    };
    
    setCartItems(prev => [...prev, cartItem]);
    updateSaleTotals([...cartItems, cartItem]);
    setSearchResults([]);
    setInventorySearchTerm('');
    setHasSearched(false);
  };

  const removeFromCart = (itemId: number) => {
    const updatedCart = cartItems.filter(item => item.inventory_item.id !== itemId);
    setCartItems(updatedCart);
    updateSaleTotals(updatedCart);
  };

  const updateCartItemPrice = (itemId: number, newPrice: number) => {
    const updatedCart = cartItems.map(item => {
      if (item.inventory_item.id === itemId) {
        const totalCost = (typeof item.inventory_item.buying_price === 'string' ? parseFloat(item.inventory_item.buying_price) : item.inventory_item.buying_price) + (typeof item.inventory_item.service_expense === 'string' ? parseFloat(item.inventory_item.service_expense) : item.inventory_item.service_expense) + (typeof item.inventory_item.package_expense === 'string' ? parseFloat(item.inventory_item.package_expense) : item.inventory_item.package_expense);
        return {
          ...item,
          selling_price: newPrice,
          profit: newPrice - totalCost
        };
      }
      return item;
    });
    setCartItems(updatedCart);
    updateSaleTotals(updatedCart);
  };

  const updateSaleTotals = (items: CartItem[]) => {
    const totalAmount = items.reduce((sum, item) => sum + item.selling_price, 0);
    const totalProfit = items.reduce((sum, item) => sum + item.profit, 0);
    const finalAmount = totalAmount;

    setNewSaleForm(prev => ({
      ...prev,
      total_amount: totalAmount,
      final_amount: finalAmount,
      total_profit: totalProfit
    }));
  };

  const lookupCustomerByPhone = async (phoneNumber: string) => {
    // Extract digits only for length check
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    if (!token || digitsOnly.length < 10) return;
    
    try {
      setIsLoadingCustomer(true);
      const response = await fetch(getApiUrl(`customers/search?phone=${encodeURIComponent(phoneNumber)}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.customer) {
          setNewSaleForm(prev => ({
            ...prev,
            customer: {
              name: data.customer.name || '',
              phone: data.customer.phone_number || phoneNumber,
              aadhar: data.customer.aadhar || '',
              address: data.customer.address || ''
            }
          }));
        }
      }
    } catch (error) {
      console.error('Customer lookup error:', error);
    } finally {
      setIsLoadingCustomer(false);
    }
  };

  // Reset new sale form
  const resetNewSaleForm = () => {
    setNewSaleForm({
      customer: {
        name: '',
        phone: '',
        aadhar: '',
        address: ''
      },
      cart_items: [],
      total_amount: 0,
      final_amount: 0,
      total_profit: 0,
      payment_method: 'cash',
      branch_id: user?.role === 'branch_manager' ? selectedBranchId || '' : '',
      sale_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setCartItems([]);
    setSearchResults([]);
    setInventorySearchTerm('');
    setHasSearched(false);
    
    // Reset scanner state
    setLastScannedValue('');
  };

  const handleSaleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (cartItems.length === 0) {
      showToast('Please add at least one item to the cart', 'warning');
      return;
    }
    
    if (!newSaleForm.customer.name || !newSaleForm.customer.phone) {
      showToast('Please fill in customer name and phone number', 'warning');
      return;
    }

    if (!newSaleForm.branch_id) {
      showToast('Please select a branch', 'warning');
      return;
    }
    
    try {
      const saleData = {
        customer: newSaleForm.customer,
        cart_items: cartItems,
        total_amount: newSaleForm.total_amount,
        final_amount: newSaleForm.final_amount,
        total_profit: newSaleForm.total_profit,
        payment_method: newSaleForm.payment_method,
        branch_id: newSaleForm.branch_id,
        sale_date: newSaleForm.sale_date,
        notes: newSaleForm.notes
      };

      const response = await fetch(`${getApiUrl("sales")}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(saleData)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          showToast('Sale created successfully!', 'success');
          resetNewSaleForm();
          setIsAddSaleModalOpen(false);
          // Refresh dashboard data
          fetchDashboardData();
        } else {
          showToast(result.message || 'Failed to create sale', 'error');
        }
      } else {
        showToast('Failed to create sale', 'error');
      }
    } catch (error) {
      console.error('Sale creation error:', error);
      showToast('An error occurred while creating the sale', 'error');
    }
  };

  const handleTimeFilterSelect = (option: string) => {
    setIsTimeFilterOpen(false);
    
    if (option === 'Custom') {
      // Set default temp dates (last 30 days) if not already set
      if (!tempStartDate || !tempEndDate) {
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);
        
        setTempEndDate(today.toISOString().split('T')[0]);
        setTempStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
      }
      setIsCustomDateModalOpen(true);
    } else {
      setSelectedTimeFilter(option);
      setIsCustomDateRange(false);
    }
  };

  // Handle custom date range modal
  const handleCustomDateApply = () => {
    if (tempStartDate && tempEndDate) {
      setCustomStartDate(tempStartDate);
      setCustomEndDate(tempEndDate);
      setSelectedTimeFilter('Custom');
      setIsCustomDateRange(true);
      setIsCustomDateModalOpen(false);
    }
  };

  const handleCustomDateCancel = () => {
    setIsCustomDateModalOpen(false);
    // Reset temp dates to current custom dates
    setTempStartDate(customStartDate);
    setTempEndDate(customEndDate);
  };

  // Function to reset dashboard filters to default
  const resetDashboardFilters = () => {
    setSelectedTimeFilter('This Month');
    setIsCustomDateRange(false);
    // Branch reset is handled by the Layout component
  };

  // Reset filters when component mounts or when navigating to dashboard
  useEffect(() => {
    // Reset time filter to default when on dashboard
    if (location.pathname === '/') {
      setSelectedTimeFilter('This Month');
    }
  }, [location.pathname]);

  // Listen for custom event to reset dashboard filters
  useEffect(() => {
    const handleResetFilters = () => {
      resetDashboardFilters();
    };

    window.addEventListener('resetDashboardFilters', handleResetFilters);
    
    return () => {
      window.removeEventListener('resetDashboardFilters', handleResetFilters);
    };
  }, []);



  // Additional useEffect hooks for inventory modal
  useEffect(() => {
    if (isAddInventoryModalOpen && token) {
      fetchSuppliers();
    }
  }, [isAddInventoryModalOpen, token]);
  
  useEffect(() => {
    if (user?.role === 'branch_manager' && selectedBranchId) {
      setSelectedModalBranch(selectedBranchId);
    }
  }, [user, selectedBranchId]);



  const PlusIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );

  const PackageIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );

  const StatCard: React.FC<{
    title: string;
    value: string;
    change?: string;
    changeType?: 'positive' | 'negative';
    subtitle?: string;
    chart?: React.ReactNode;
    helpText?: string;
  }> = ({ title, value, change, changeType, subtitle, chart, helpText }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-gray-600">{title}</h3>
          <InfoIcon helpText={helpText} />
        </div>
      </div>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
          {change && (
            <div className={`flex items-center gap-1 text-sm ${
              changeType === 'positive' ? 'text-green-600' : 'text-red-600'
            }`}>
              {changeType === 'positive' ? <TrendingUpIcon /> : <TrendingDownIcon />}
              {change}
            </div>
          )}
          {subtitle && (
            <div className="text-sm text-gray-500">{subtitle}</div>
          )}
        </div>
        {chart && <div className="ml-4">{chart}</div>}
      </div>
    </div>
  );

  // Pagination Component
  const Pagination: React.FC<{
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    totalRecords: number;
    itemsPerPage: number;
    onItemsPerPageChange: (items: number) => void;
    currentRecords: number;
  }> = ({ currentPage, totalPages, onPageChange, totalRecords, itemsPerPage, onItemsPerPageChange, currentRecords }) => (
    <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
      <div className="flex items-center gap-2 mb-4 sm:mb-0">
        <span className="text-sm text-gray-700">Show</span>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
        </select>
        <span className="text-sm text-gray-700">
          entries (Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords})
        </span>
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        
        {/* Page Numbers */}
        {[...Array(totalPages)].map((_, index) => {
          const page = index + 1;
          const isCurrentPage = page === currentPage;
          
          // Show first page, last page, current page and adjacent pages
          if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-3 py-1 text-sm border rounded ${
                  isCurrentPage
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'border-gray-300 hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            );
          }
          
          // Show ellipsis
          if (page === currentPage - 2 || page === currentPage + 2) {
            return <span key={page} className="px-2 text-sm text-gray-500">...</span>;
          }
          
          return null;
        })}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );

  // Show loading spinner while authentication is loading
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" text="Loading dashboard..." />
      </div>
    );
  }

  // Check permissions after user is loaded
  if (!checkPermission(user?.role, 'dashboard')) {
    return <AccessDenied resource="Dashboard" />;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Summary of Sales and Inventory Management</p>
        </div>
        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
          {/* Show Balances Toggle */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Show Balances</span>
            <button
              onClick={handleShowBalancesToggle}
              disabled={isLoadingBalancesSetting}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                showBalances ? 'bg-primary-500' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  showBalances ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          <div className="relative" ref={timeFilterRef}>
            <button 
              onClick={() => setIsTimeFilterOpen(!isTimeFilterOpen)}
              className="w-full sm:w-auto flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
            >
              <span className="text-sm font-medium text-gray-700">{selectedTimeFilter}</span>
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${isTimeFilterOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Time Filter Dropdown */}
            {isTimeFilterOpen && (
              <div className="absolute left-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                {timeFilterOptions.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleTimeFilterSelect(option)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                      selectedTimeFilter === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}

          </div>
          <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-3">
            <button 
              onClick={() => setIsAddInventoryModalOpen(true)}
              className="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 transition-colors h-10"
            >
              <PackageIcon />
              <span className="whitespace-nowrap">Add Inventory</span>
            </button>
            <button 
              onClick={() => setIsAddSaleModalOpen(true)}
              className="w-full sm:w-auto px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 flex items-center justify-center gap-2 transition-colors h-10"
            >
              <PlusIcon />
              <span className="whitespace-nowrap">Add New Sale</span>
            </button>
          </div>
        </div>
      </div>

      {/* Branch Filter Info */}
      {!isAllBranches && selectedBranchId && user?.role !== 'branch_manager' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Branch Filter Active
              </h3>
              <div className="mt-1 text-sm text-blue-700">
                Showing only dashboard data from <strong>{selectedBranchName}</strong>. 
                Select "All Branches" to see analytics from all locations.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Financial Summaries */}
      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title={
              isLoadingDashboard 
                ? "Opening Balance"
                : dashboardData?.balances?.isToday 
                  ? "Opening Balance"
                  : "Opening Balance ⚠️"
            }
            value={isLoadingDashboard ? "Loading..." : formatBalance(dashboardData?.balances?.openingBalance || 0)}
            subtitle={
              isLoadingDashboard 
                ? "Loading..." 
                : dashboardData?.balances?.isToday 
                  ? "Today's Opening Balance"
                  : `Last Available (${new Date(dashboardData?.balances?.balanceDate || '').toLocaleDateString('en-IN', { 
                      day: 'numeric', 
                      month: 'short',
                      year: 'numeric'
                    })})`
            }
            helpText={
              dashboardData?.balances?.isToday
                ? "Shows today's opening cash/bank balance. This amount automatically updates when you make purchases (decreases balance) or sales (increases balance). Helps track daily cash flow and available funds."
                : "No opening balance recorded for today. Showing the most recent available opening balance from previous date. Consider updating today's opening balance for accurate tracking."
            }
          />
          <StatCard
            title="Total Revenue (Customers)"
            value={isLoadingDashboard ? "Loading..." : formatBalance(dashboardData?.customers?.totalRevenue || 0)}
            change={isLoadingDashboard ? "..." : `${dashboardData?.customers?.revenueChange >= 0 ? '+' : ''}${(dashboardData?.customers?.revenueChange || 0).toFixed(1)}%`}
            changeType={dashboardData?.customers?.revenueChange >= 0 ? "positive" : "negative"}
            subtitle={getComparisonLabel(selectedTimeFilter)}
            helpText="Total final amount earned from customer sales (after discounts). This includes all completed sales transactions from individual customers during the selected time period. Percentage shows comparison with previous period."
          />
          <StatCard
            title="Total Profit (Customers)"
            value={isLoadingDashboard ? "Loading..." : formatBalance(dashboardData?.customers?.totalProfit || 0)}
            change={isLoadingDashboard ? "..." : `${dashboardData?.customers?.profitChange >= 0 ? '+' : ''}${(dashboardData?.customers?.profitChange || 0).toFixed(1)}%`}
            changeType={dashboardData?.customers?.profitChange >= 0 ? "positive" : "negative"}
            subtitle={getComparisonLabel(selectedTimeFilter)}
            helpText="Net profit from customer sales after deducting buying price, service expenses, and package expenses. Formula: Selling Price - (Buying Price + Service Expense + Package Expense). This shows actual profit margins from customer transactions."
          />
          <StatCard
            title="Total Sales (Customers)"
            value={isLoadingDashboard ? "Loading..." : `${dashboardData?.customers?.totalSales || 0} sales`}
            change={isLoadingDashboard ? "..." : `${dashboardData?.customers?.salesChange >= 0 ? '+' : ''}${(dashboardData?.customers?.salesChange || 0).toFixed(1)}%`}
            changeType={dashboardData?.customers?.salesChange >= 0 ? "positive" : "negative"}
            subtitle={getComparisonLabel(selectedTimeFilter)}
            helpText="Total number of sales transactions completed with individual customers during the selected period. Each invoice/transaction is counted as one sale, regardless of how many items were purchased in that transaction."
          />
        </div>
      </div>

      {/* Dealer Financial Summaries */}
      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Revenue (Dealers)"
            value={isLoadingDashboard ? "Loading..." : formatBalance(dashboardData?.dealers?.totalRevenue || 0)}
            change={isLoadingDashboard ? "..." : `${dashboardData?.dealers?.revenueChange >= 0 ? '+' : ''}${(dashboardData?.dealers?.revenueChange || 0).toFixed(1)}%`}
            changeType={dashboardData?.dealers?.revenueChange >= 0 ? "positive" : "negative"}
            subtitle={getComparisonLabel(selectedTimeFilter)}
            helpText="Total sales amount from dealer transactions during the selected period. This includes bulk sales to business dealers and resellers. Revenue from both completed and partially paid dealer transactions."
          />
          <StatCard
            title="Total Profit (Dealers)"
            value={isLoadingDashboard ? "Loading..." : formatBalance(dashboardData?.dealers?.totalProfit || 0)}
            change={isLoadingDashboard ? "..." : `${dashboardData?.dealers?.profitChange >= 0 ? '+' : ''}${(dashboardData?.dealers?.profitChange || 0).toFixed(1)}%`}
            changeType={dashboardData?.dealers?.profitChange >= 0 ? "positive" : "negative"}
            subtitle={getComparisonLabel(selectedTimeFilter)}
            helpText="Net profit earned from dealer sales after deducting all costs. Calculated by subtracting buying price, service expenses, and package expenses from dealer selling prices. Shows profitability from B2B transactions."
          />
          <StatCard
            title="Total Outstanding (Dealers)"
            value={isLoadingDashboard ? "Loading..." : formatBalance(dashboardData?.dealers?.totalOutstanding || 0)}
            subtitle="Total Pending Amount"
            helpText="Total pending payment amount from all dealers across all transactions. This includes partial payments and fully pending amounts. Tracks money owed to your business by dealer customers for delivered products."
          />
          <StatCard
            title="Total Sales (Dealers)"
            value={isLoadingDashboard ? "Loading..." : `${dashboardData?.dealers?.totalSales || 0} sales`}
            change={isLoadingDashboard ? "..." : `${dashboardData?.dealers?.salesChange >= 0 ? '+' : ''}${(dashboardData?.dealers?.salesChange || 0).toFixed(1)}%`}
            changeType={dashboardData?.dealers?.salesChange >= 0 ? "positive" : "negative"}
            subtitle={getComparisonLabel(selectedTimeFilter)}
            helpText="Total number of dealer transactions completed during the selected period. Each bulk order or dealer invoice is counted as one sale, regardless of payment status (pending, partial, or fully paid)."
          />
        </div>
      </div>


      {/* Charts and Additional Info */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Monthly Chart */}
        <div className="xl:col-span-2 bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
          <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                <span className="text-sm text-gray-600">{selectedTimeFilter}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-primary-400 rounded-full"></div>
                <span className="text-sm text-gray-600">{
                  selectedTimeFilter === 'This Month' ? 'Last Month' :
                  selectedTimeFilter === 'Last Month' ? 'Previous Month' :
                  selectedTimeFilter === 'This Year' ? 'Last Year' :
                  selectedTimeFilter === 'Last Year' ? 'Previous Year' :
                  selectedTimeFilter === 'Custom' ? `${customStartDate} to ${customEndDate}` :
                  'Previous Period'
                }</span>
              </div>
            </div>
            <button>
              <MoreHorizontalIcon />
            </button>
          </div>
          <div className="h-48 sm:h-64">
            <Bar 
              data={monthlyData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed.y;
                        return `${context.dataset.label}: ${formatCurrency(value * 1000)}`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    grid: {
                      display: false,
                    },
                    border: {
                      display: false,
                    },
                  },
                  y: {
                    grid: {
                      display: true,
                      color: '#f1f5f9',
                    },
                    border: {
                      display: false,
                    },
                    ticks: {
                      display: true,
                      callback: function(value) {
                        return formatCurrency(Number(value) * 1000);
                      }
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Customer vs Dealer Sales */}
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Customer vs Deals Sales</h3>
            <button>
              <MoreHorizontalIcon />
            </button>
          </div>
          <div className="h-40 mb-6">
            <Doughnut 
              key={showBalances ? 'show' : 'hide'} // Force re-render when toggle changes
              data={customerVsDealerData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed;
                        return `${context.label}: ${formatCurrency(value)}`;
                      }
                    }
                  }
                },
                cutout: '60%',
              }}
            />
          </div>
          <div className="text-center mb-4">
            <div className="text-2xl font-bold">
              {isLoadingDashboard ? "Loading..." : formatBalance(
                (dashboardData && !isLoadingDashboard) 
                  ? ((dashboardData?.chartData?.customerSales || 0) + (dashboardData?.chartData?.dealerSales || 0))
                  : 552000
              )}
            </div>
            <div className="text-sm text-gray-500">Total Sales Volume</div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Customer Sales</span>
              </div>
              <span className="text-sm font-medium">
                {isLoadingDashboard ? "Loading..." : formatBalance(
                  (dashboardData && !isLoadingDashboard) 
                    ? (dashboardData?.chartData?.customerSales || 0)
                    : 385000
                )}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-primary-400 rounded-full"></div>
                <span className="text-sm text-gray-600">Dealer Sales</span>
              </div>
              <span className="text-sm font-medium">
                {isLoadingDashboard ? "Loading..." : formatBalance(
                  (dashboardData && !isLoadingDashboard) 
                    ? (dashboardData?.chartData?.dealerSales || 0)
                    : 167000
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Tables */}
      <div className="space-y-6">
        {/* Tables Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Inventory Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Recent Inventory</h3>
                <p className="text-sm text-gray-600 mt-1">
                  New inventory items added {inventoryDayFilter === 'today' ? 'today' : 'yesterday'}
                </p>
              </div>
              <div className="relative">
                <button 
                  onClick={() => setIsInventoryDayFilterOpen(!isInventoryDayFilterOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors"
                >
                  <span className="font-medium text-gray-700">
                    {inventoryDayFilter === 'today' ? 'Today' : 'Yesterday'}
                  </span>
                  <svg className={`w-3 h-3 text-gray-400 transition-transform ${isInventoryDayFilterOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isInventoryDayFilterOpen && (
                  <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <button
                      onClick={() => {
                        setInventoryDayFilter('today');
                        setIsInventoryDayFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        inventoryDayFilter === 'today' ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => {
                        setInventoryDayFilter('yesterday');
                        setIsInventoryDayFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        inventoryDayFilter === 'yesterday' ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      Yesterday
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              {isLoadingInventoryActivity ? (
                <div className="p-8 text-center">
                  <LoadingSpinner size="sm" text={`Loading ${inventoryDayFilter} inventory...`} />
                </div>
              ) : dayInventoryData.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dayInventoryData.slice(0, 5).map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {item.phone_model}
                            </div>
                            <div className="text-sm text-gray-500">
                              {item.inventory_id}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatCurrency(item.buying_price || 0)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            item.status === 'ready' ? 'bg-green-100 text-green-800' :
                            item.status === 'service' ? 'bg-yellow-100 text-yellow-800' :
                            item.status === 'reserved' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <p>No inventory added {inventoryDayFilter === 'today' ? 'today' : 'yesterday'}</p>
                </div>
              )}
            </div>
            {dayInventoryData.length > 5 && (
              <div className="px-6 py-3 bg-gray-50 text-center">
                <span className="text-sm text-gray-600">
                  Showing 5 of {dayInventoryData.length} items
                </span>
              </div>
            )}
          </div>

          {/* Recent Sales Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Recent Sales</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Customer sales made {salesDayFilter === 'today' ? 'today' : 'yesterday'}
                </p>
              </div>
              <div className="relative">
                <button 
                  onClick={() => setIsSalesDayFilterOpen(!isSalesDayFilterOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors"
                >
                  <span className="font-medium text-gray-700">
                    {salesDayFilter === 'today' ? 'Today' : 'Yesterday'}
                  </span>
                  <svg className={`w-3 h-3 text-gray-400 transition-transform ${isSalesDayFilterOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isSalesDayFilterOpen && (
                  <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <button
                      onClick={() => {
                        setSalesDayFilter('today');
                        setIsSalesDayFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        salesDayFilter === 'today' ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => {
                        setSalesDayFilter('yesterday');
                        setIsSalesDayFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        salesDayFilter === 'yesterday' ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      Yesterday
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              {isLoadingSalesActivity ? (
                <div className="p-8 text-center">
                  <LoadingSpinner size="sm" text={`Loading ${salesDayFilter} sales...`} />
                </div>
              ) : daySalesData.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Items
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {daySalesData.slice(0, 5).map((sale) => (
                      <tr key={sale.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {sale.customers?.name || sale.customer_name || 'Walk-in Customer'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {sale.invoice_id}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatCurrency(sale.final_amount || 0)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {sale.items_count || 1} item{(sale.items_count || 1) > 1 ? 's' : ''}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  <p>No sales made {salesDayFilter === 'today' ? 'today' : 'yesterday'}</p>
                </div>
              )}
            </div>
            {daySalesData.length > 5 && (
              <div className="px-6 py-3 bg-gray-50 text-center">
                <span className="text-sm text-gray-600">
                  Showing 5 of {daySalesData.length} items
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add New Sale Modal */}
      {isAddSaleModalOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => {
            setIsAddSaleModalOpen(false);
            resetNewSaleForm();
          }}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Add New Sale</h2>
              <button
                onClick={() => {
                  setIsAddSaleModalOpen(false);
                  resetNewSaleForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSaleSubmit} className="space-y-6">
              {/* Inventory Search */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Add Items to Cart
                  </label>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" />
                    </svg>
                    <span className="text-sm text-green-600 font-medium">Scanner Ready</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={inventorySearchTerm}
                      onChange={(e) => {
                        // Auto-trim input value to handle pasted content with spaces
                        const trimmedValue = e.target.value.trim();
                        setInventorySearchTerm(trimmedValue);
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleInventorySearch())}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Search by IMEI, Inventory ID, Model, or scan barcode..."
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleInventorySearch}
                    className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors h-10 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Search
                  </button>
                </div>

                {/* Barcode Scanner - Always Active */}
                <BarcodeScanner
                  onScan={handleBarcodeScan}
                  isActive={isAddSaleModalOpen}
                  className="hidden"
                />
                
                {/* Search Results */}
                {isLoadingInventory && (
                  <div className="mt-3 p-4 text-center">
                    <LoadingSpinner size="sm" text="Loading inventory..." />
                  </div>
                )}
                
                {!isLoadingInventory && searchResults.length > 0 && (
                  <div className="mt-3 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                    <div className="space-y-2 p-2">
                      {searchResults.map((item) => (
                        <div key={item.id} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 mb-1">{item.phone_model}</p>
                            <p className="text-xs text-gray-500 mb-1">IMEI: {item.imei_mac_address}</p>
                            <p className="text-xs text-gray-400">ID: {item.inventory_id}</p>
                            <p className="text-xs text-gray-400 mt-1">Minimum Sellable Cost: {formatCurrency(typeof item.selling_price === 'string' ? parseFloat(item.selling_price) : item.selling_price || 0)}</p>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <div className="relative">
                              <span className="absolute left-3 top-2.5 text-sm text-gray-500">{getCurrencySymbol()}</span>
                              <input
                                type="text"
                                placeholder="Price"
                                className="w-36 pl-8 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                const input = e.currentTarget.previousElementSibling?.querySelector('input') as HTMLInputElement;
                                const price = parseFloat(input.value);
                                if (price > 0) {
                                  addToCart(item, price);
                                  input.value = '';
                                }
                              }}
                              className="px-4 py-2.5 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                            >
                              Add to Cart
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {!isLoadingInventory && searchResults.length === 0 && hasSearched && inventorySearchTerm && (
                  <div className="mt-3 p-3 text-sm text-gray-500 text-center border border-gray-200 rounded-lg">
                    No available inventory items found. Try searching with different keywords.
                  </div>
                )}
              </div>

              {/* Cart Items */}
              {cartItems.length > 0 && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Selected Items ({cartItems.length})</h3>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {cartItems.map((cartItem) => (
                      <div key={cartItem.inventory_item.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        {/* Item Details */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 mb-1">{cartItem.inventory_item.phone_model}</p>
                          <p className="text-xs text-gray-500 mb-1">IMEI: {cartItem.inventory_item.imei_mac_address}</p>
                          <p className="text-xs text-gray-400">ID: {cartItem.inventory_item.inventory_id}</p>
                        </div>
                        
                        {/* Selling Price Input */}
                        <div className="flex-shrink-0">
                          <label className="block text-xs text-gray-500 mb-1">Selling Price</label>
                          <div className="relative">
                            <span className="absolute left-3 top-2.5 text-sm text-gray-500">{getCurrencySymbol()}</span>
                            <input
                              type="number"
                              value={cartItem.selling_price}
                              onChange={(e) => updateCartItemPrice(cartItem.inventory_item.id, parseFloat(e.target.value) || 0)}
                              className="w-36 pl-8 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="0"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        
                        {/* Delete Button */}
                        <div className="flex-shrink-0 flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => removeFromCart(cartItem.inventory_item.id)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove item"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Customer Information */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        value={newSaleForm.customer.phone}
                        onChange={(e) => {
                          const phoneValue = e.target.value;
                          const digitsOnly = phoneValue.replace(/\D/g, '');
                          
                          setNewSaleForm(prev => ({
                            ...prev,
                            customer: { ...prev.customer, phone: phoneValue }
                          }));
                          
                          // Trigger lookup when phone number has 10+ digits
                          if (digitsOnly.length >= 10) {
                            lookupCustomerByPhone(phoneValue);
                          } else if (phoneValue.length === 0) {
                            // Reset customer details if phone is cleared
                            setNewSaleForm(prev => ({
                              ...prev,
                              customer: {
                                ...prev.customer,
                                name: '',
                                aadhar: '',
                                address: ''
                              }
                            }));
                          }
                        }}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                        placeholder="+91 XXXXX XXXXX"
                        required
                      />
                      {isLoadingCustomer && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <LoadingSpinner size="sm" color="primary" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      value={newSaleForm.customer.name}
                      onChange={(e) => setNewSaleForm(prev => ({
                        ...prev,
                        customer: { ...prev.customer, name: e.target.value }
                      }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter customer name"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Aadhar Number (Optional)
                    </label>
                    <input
                      type="text"
                      value={newSaleForm.customer.aadhar}
                      onChange={(e) => setNewSaleForm(prev => ({
                        ...prev,
                        customer: { ...prev.customer, aadhar: e.target.value }
                      }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="XXXX-XXXX-XXXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address (Optional)
                    </label>
                    <input
                      type="text"
                      value={newSaleForm.customer.address}
                      onChange={(e) => setNewSaleForm(prev => ({
                        ...prev,
                        customer: { ...prev.customer, address: e.target.value }
                      }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter customer address"
                    />
                  </div>
                </div>
              </div>
              
              {/* Sale Information */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Sale Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Method *
                    </label>
                    <div className="relative">
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsModalSalePaymentMethodDropdownOpen(!isModalSalePaymentMethodDropdownOpen);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      >
                        <span className="text-sm text-gray-700">{newSaleForm.payment_method.charAt(0).toUpperCase() + newSaleForm.payment_method.slice(1).replace('_', ' ')}</span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isModalSalePaymentMethodDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {isModalSalePaymentMethodDropdownOpen && (
                        <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                          {['cash', 'upi', 'bank_transfer', 'card', 'cheque'].map((option, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setNewSaleForm(prev => ({ ...prev, payment_method: option }));
                                setIsModalSalePaymentMethodDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                newSaleForm.payment_method === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {option.charAt(0).toUpperCase() + option.slice(1).replace('_', ' ')}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sale Date *
                    </label>
                    <input
                      type="date"
                      value={newSaleForm.sale_date}
                      onChange={(e) => setNewSaleForm(prev => ({ ...prev, sale_date: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Branch *
                    </label>
                    <div className="relative">
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Don't open dropdown for branch managers
                          if (user?.role === 'branch_manager') return;
                          setIsModalSaleBranchDropdownOpen(!isModalSaleBranchDropdownOpen);
                        }}
                        disabled={user?.role === 'branch_manager'}
                        className={`w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors h-10 ${
                          user?.role === 'branch_manager' 
                            ? 'bg-gray-100 cursor-not-allowed opacity-70' 
                            : 'bg-white hover:border-gray-300'
                        }`}
                      >
                        <span className="text-sm text-gray-700">
                          {newSaleForm.branch_id ? getBranchNameById(newSaleForm.branch_id) : 'Select Branch'}
                          {user?.role === 'branch_manager' && newSaleForm.branch_id && (
                            <span className="text-xs text-gray-500 ml-1">(Your Branch)</span>
                          )}
                        </span>
                        {user?.role !== 'branch_manager' && (
                          <svg className={`w-4 h-4 text-gray-400 transition-transform ${isModalSaleBranchDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </button>
                      
                      {isModalSaleBranchDropdownOpen && user?.role !== 'branch_manager' && (
                        <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                          {branches.map((branch) => (
                            <button
                              key={branch.id}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setNewSaleForm(prev => ({ ...prev, branch_id: branch.id }));
                                setIsModalSaleBranchDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                newSaleForm.branch_id === branch.id ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {branch.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sale Summary */}
                {cartItems.length > 0 && (
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Sale Summary</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Amount:</span>
                        <span className="font-medium">{formatCurrency(newSaleForm.total_amount)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Profit:</span>
                        <span className="font-medium text-green-600">{formatCurrency(newSaleForm.total_profit)}</span>
                      </div>
                      <div className="border-t pt-2">
                        <div className="flex justify-between text-base font-semibold">
                          <span>Final Amount:</span>
                          <span>{formatCurrency(newSaleForm.final_amount)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Sale Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sale Notes (Optional)
                </label>
                <textarea
                  rows={3}
                  value={newSaleForm.notes}
                  onChange={(e) => setNewSaleForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors resize-none"
                  placeholder="Add any notes about the sale..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => setIsAddSaleModalOpen(false)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Create Sale
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add New Inventory Modal */}
      {isAddInventoryModalOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setIsAddInventoryModalOpen(false)}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Add New Inventory Item</h2>
              <button
                onClick={handleInventoryModalClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form className="space-y-6" onClick={closeAllModalDropdowns} onSubmit={handleSubmitInventory}>
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Model *
                  </label>
                  <input
                    type="text"
                    value={inventoryFormData.phone_model}
                    onChange={(e) => setInventoryFormData({...inventoryFormData, phone_model: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                    placeholder="Enter device model"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        closeAllModalDropdowns();
                        setIsModalCategoryDropdownOpen(!isModalCategoryDropdownOpen);
                      }}
                      className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                    >
                      <span className="text-sm text-gray-700">{getSelectedCategoryLabel()}</span>
                      <svg className={`w-4 h-4 text-gray-400 transition-transform ${isModalCategoryDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {isModalCategoryDropdownOpen && (
                      <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                        {modalCategoryOptions.map((option, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedModalCategory(option.value);
                              setIsModalCategoryDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                              selectedModalCategory === option.value ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {(selectedModalCategory === 'mobile_phone' || selectedModalCategory === 'tablet') ? 'IMEI Number *' : 'IMEI/MAC Address *'}
                  </label>
                  <input
                    type="text"
                    value={inventoryFormData.imei_mac_address}
                    onChange={(e) => setInventoryFormData({...inventoryFormData, imei_mac_address: e.target.value})}
                    onBlur={validateImeiOnBlur}
                    onFocus={() => setImeiError('')}
                    className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 bg-white transition-colors h-10 ${
                      imeiError ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 focus:ring-primary-500 hover:border-gray-300'
                    }`}
                    placeholder={(selectedModalCategory === 'mobile_phone' || selectedModalCategory === 'tablet') ? 'Enter 15-digit IMEI number' : 'Enter IMEI or MAC address'}
                    required
                  />
                  {imeiError && (
                    <p className="text-xs mt-1 text-red-600">{imeiError}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Branch *
                  </label>
                  <div className="relative">
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Don't open dropdown for branch managers
                        if (user?.role === 'branch_manager') return;
                        closeAllModalDropdowns();
                        setIsModalBranchDropdownOpen(!isModalBranchDropdownOpen);
                      }}
                      disabled={user?.role === 'branch_manager'}
                      className={`w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors h-10 ${
                        user?.role === 'branch_manager' 
                          ? 'bg-gray-100 cursor-not-allowed opacity-70' 
                          : 'bg-white hover:border-gray-300'
                      }`}
                    >
                      <span className="text-sm text-gray-700">
                        {selectedModalBranch ? getBranchNameById(selectedModalBranch) : 'Select Branch'}
                        {user?.role === 'branch_manager' && selectedModalBranch && (
                          <span className="text-xs text-gray-500 ml-1">(Your Branch)</span>
                        )}
                      </span>
                      {user?.role !== 'branch_manager' && (
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isModalBranchDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>
                    
                    {isModalBranchDropdownOpen && user?.role !== 'branch_manager' && (
                      <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                        {branches.map((branch) => (
                          <button
                            key={branch.id}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedModalBranch(branch.id);
                              setIsModalBranchDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                              selectedModalBranch === branch.id ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {branch.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buying Price *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-500">{getCurrencySymbol()}</span>
                    <input
                      type="text"
                      value={inventoryFormData.buying_price}
                      onChange={(e) => setInventoryFormData({...inventoryFormData, buying_price: e.target.value})}
                      className="w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="0"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selling Price (Min Price)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-500">{getCurrencySymbol()}</span>
                    <input
                      type="text"
                      value={inventoryFormData.selling_price}
                      onChange={(e) => setInventoryFormData({...inventoryFormData, selling_price: e.target.value})}
                      className="w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status *
                  </label>
                  <div className="relative">
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        closeAllModalDropdowns();
                        setIsModalStatusDropdownOpen(!isModalStatusDropdownOpen);
                      }}
                      className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                    >
                      <span className="text-sm text-gray-700">{getSelectedStatusLabel()}</span>
                      <svg className={`w-4 h-4 text-gray-400 transition-transform ${isModalStatusDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {isModalStatusDropdownOpen && (
                      <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                        {modalStatusOptions.map((option, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedModalStatus(option.value);
                              setIsModalStatusDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                              selectedModalStatus === option.value ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Supplier Information */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Supplier Information (Optional)</h3>
                
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isNewSupplier}
                      onChange={(e) => setIsNewSupplier(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Add new supplier</span>
                  </label>
                </div>

                {!isNewSupplier && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Existing Supplier
                    </label>

                    {/* Selected Supplier Display */}
                    {selectedSupplier && (() => {
                      const supplier = suppliers.find(s => s.id === selectedSupplier);
                      return (
                        <div className="mb-3 p-3 bg-primary-50 border border-primary-200 rounded-lg flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-primary-800">{supplier?.name}</p>
                            <div className="mt-1 space-y-0.5">
                              <p className="text-xs text-primary-600">
                                <span className="text-primary-400">Phone:</span> {supplier?.phone_number || 'Not available'}
                              </p>
                              <p className="text-xs text-primary-600">
                                <span className="text-primary-400">Aadhar:</span> {supplier?.aadhar || 'Not available'}
                              </p>
                              <p className="text-xs text-primary-600">
                                <span className="text-primary-400">Address:</span> {supplier?.address || 'Not available'}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleClearSupplier}
                            className="p-1.5 text-primary-600 hover:text-primary-800 hover:bg-primary-100 rounded-lg transition-colors"
                            title="Clear selection"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      );
                    })()}

                    {/* Search Input */}
                    {!selectedSupplier && (
                      <>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                            </div>
                            <input
                              type="text"
                              value={supplierSearchTerm}
                              onChange={(e) => setSupplierSearchTerm(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleSupplierSearch())}
                              className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                              placeholder="Search by name, phone, or aadhar..."
                            />
                          </div>
                          <button
                            type="button"
                            onClick={handleSupplierSearch}
                            className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors h-10 flex items-center gap-2 whitespace-nowrap"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            Search
                          </button>
                        </div>

                        {/* Search Results */}
                        {supplierSearchResults.length > 0 && (
                          <div className="mt-3 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                            <div className="space-y-2 p-2">
                              {supplierSearchResults.map((supplier) => (
                                <div
                                  key={supplier.id}
                                  className="flex items-start justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900">{supplier.name}</p>
                                    <div className="mt-1 space-y-0.5">
                                      <p className="text-xs text-gray-500">
                                        <span className="text-gray-400">Phone:</span> {supplier.phone_number || 'Not available'}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        <span className="text-gray-400">Aadhar:</span> {supplier.aadhar || 'Not available'}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        <span className="text-gray-400">Address:</span> {supplier.address || 'Not available'}
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedSupplier(supplier.id);
                                      setSupplierSearchTerm('');
                                      setSupplierSearchResults([]);
                                      setHasSearchedSupplier(false);
                                    }}
                                    className="ml-3 px-3 py-1.5 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                                  >
                                    Select
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* No Results */}
                        {supplierSearchResults.length === 0 && hasSearchedSupplier && supplierSearchTerm && (
                          <div className="mt-3 p-3 text-sm text-gray-500 text-center border border-gray-200 rounded-lg">
                            No suppliers found. Try a different search term or add a new supplier.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {isNewSupplier && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Supplier Name *
                      </label>
                      <input
                        type="text"
                        value={inventoryFormData.supplier_name}
                        onChange={(e) => setInventoryFormData({...inventoryFormData, supplier_name: e.target.value})}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                        placeholder="Enter supplier name"
                        required={isNewSupplier}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Supplier Phone *
                      </label>
                      <input
                        type="tel"
                        value={inventoryFormData.supplier_phone}
                        onChange={(e) => handleSupplierPhoneChange(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                        placeholder="Enter 10-digit phone number"
                        required={isNewSupplier}
                      />
                      <p className="text-xs text-gray-400 mt-1">Auto-fills if supplier exists</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Supplier Aadhar
                      </label>
                      <input
                        type="text"
                        value={inventoryFormData.supplier_aadhar}
                        onChange={(e) => setInventoryFormData({...inventoryFormData, supplier_aadhar: e.target.value})}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                        placeholder="XXXX-XXXX-XXXX"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Supplier Address
                      </label>
                      <input
                        type="text"
                        value={inventoryFormData.supplier_address}
                        onChange={(e) => setInventoryFormData({...inventoryFormData, supplier_address: e.target.value})}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                        placeholder="Enter supplier address"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Method */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Method
                    </label>
                    <div className="relative">
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          closeAllModalDropdowns();
                          setIsModalPaymentMethodDropdownOpen(!isModalPaymentMethodDropdownOpen);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      >
                        <span className="text-sm text-gray-700">{getSelectedPaymentLabel()}</span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isModalPaymentMethodDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {isModalPaymentMethodDropdownOpen && (
                        <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                          {modalPaymentMethodOptions.map((option, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedModalPaymentMethod(option.value);
                                setIsModalPaymentMethodDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                selectedModalPaymentMethod === option.value ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Acquisition Notes
                </label>
                <textarea
                  rows={3}
                  value={inventoryFormData.acquisition_notes}
                  onChange={(e) => setInventoryFormData({...inventoryFormData, acquisition_notes: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors resize-none"
                  placeholder="Enter any notes about the item condition, issues, etc."
                ></textarea>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={handleInventoryModalClose}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={isLoadingInventorySubmit}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
                  disabled={isLoadingInventorySubmit}
                >
                  {isLoadingInventorySubmit ? 'Adding...' : 'Add Inventory Item'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Custom Date Range Modal */}
      {isCustomDateModalOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl p-6 w-full max-w-md m-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Select Date Range</h2>
              <button
                onClick={handleCustomDateCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Date *
                </label>
                <input
                  type="date"
                  value={tempStartDate}
                  onChange={(e) => setTempStartDate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To Date *
                </label>
                <input
                  type="date"
                  value={tempEndDate}
                  onChange={(e) => setTempEndDate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              
              {/* Date range info */}
              {tempStartDate && tempEndDate && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Selected range: <span className="font-medium">{tempStartDate}</span> to <span className="font-medium">{tempEndDate}</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {(() => {
                      const start = new Date(tempStartDate);
                      const end = new Date(tempEndDate);
                      const diffTime = Math.abs(end.getTime() - start.getTime());
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                      return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
                    })()}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={handleCustomDateCancel}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCustomDateApply}
                disabled={!tempStartDate || !tempEndDate}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply Date Range
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Toast Component */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />
    </div>
  );
};

export default Dashboard;